// Unico modo di chiamare le API dal front-end: $fetch con cookie e token già inclusi, POST di default.
// Eccezione documentata: le pagine prima del login (login, recupero password, verifica dispositivo) usano $fetch.

export interface OpzioniAuthFetch {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, unknown>;
  headers?: Record<string, string>;
  responseType?: "json" | "text" | "blob" | "arrayBuffer" | "stream";
  // Chiave per annullare la richiesta precedente identica (di default l'URL)
  cancelKey?: string;
  // Se vero, una nuova richiesta con la stessa chiave annulla quella ancora in corso
  autoCancel?: boolean;
  // Se vero, un 401 non porta al login con l'avviso «sessione scaduta» (es. la chiamata di logout: la sessione la chiude già chi chiama)
  ignora401?: boolean;
  [altra: string]: unknown;
}

// $fetch con le rotte tipizzate di Nitro (centinaia di endpoint) manda in tilt TypeScript: qui l'URL è una stringa qualunque.
type FetchLibero = (url: string, opzioni: Record<string, unknown>) => Promise<unknown>;

// Stato HTTP di un errore di $fetch (ofetch lo mette in `statusCode` o `status`)
const statoHttp = (errore: unknown): number | undefined => {
  const e = errore as { statusCode?: number; status?: number } | null;
  return e?.statusCode ?? e?.status;
};

// Token di sessione oggi nel cookie (il cookie si legge di nuovo: può essere cambiato mentre la richiesta era in volo)
const tokenNelCookie = (nome: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const trovato = document.cookie.split("; ").find((c) => c.startsWith(`${nome}=`));
  return trovato ? decodeURIComponent(trovato.slice(nome.length + 1)) : undefined;
};

export const useAuthFetch = () => {
  const config = useRuntimeConfig();
  const BASE_PROGETTO = config.public.base_progetto;
  const controllers = new Map<string, AbortController>();

  /**
   * Wrapper di $fetch con header di autenticazione già inclusi.
   * Il tipo della risposta lo sceglie chi chiama: `authFetch<MiaRisposta>(url, ...)`. La risposta può contenere
   * `{ error }` anche con HTTP 200: controllarla sempre.
   */
  async function authFetch<T = any>(url: string, options: OpzioniAuthFetch = {}): Promise<T> {
    const token = useCookie(`token${BASE_PROGETTO}`);
    const { cancelKey: chiaveEsplicita, autoCancel, ignora401, ...resto } = options;
    const tokenInviato = token.value ?? undefined;
    const cancelKey = chiaveEsplicita || url;

    if (autoCancel) controllers.get(cancelKey)?.abort("Request aborted by a new identical request");

    const controller = new AbortController();
    controllers.set(cancelKey, controller);

    try {
      return (await (($fetch as unknown) as FetchLibero)(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.value}`,
        },
        ...resto,
        // il segnale è sempre quello interno: si annulla con `autoCancel`, `abortRequest` o `abortAll`
        signal: controller.signal,
      })) as T;
    } catch (errore) {
      // 401: il token non vale più → login con avviso (una volta sola, anche con più richieste in volo). Se nel frattempo c'è stato un nuovo
      // accesso (il cookie ha un altro token) il 401 è di una richiesta partita col token vecchio: non riguarda la sessione nuova.
      if (statoHttp(errore) === 401 && !ignora401 && import.meta.client && !window.location.pathname.startsWith("/login") && tokenNelCookie(`token${BASE_PROGETTO}`) === tokenInviato) {
        void useAuthStore().sessioneScaduta();
      }
      throw errore;
    } finally {
      if (controllers.get(cancelKey) === controller) controllers.delete(cancelKey);
    }
  }

  // Annullamento forzato da fuori (es. cambio pagina)
  const abortRequest = (key: string): void => {
    controllers.get(key)?.abort("Manual abort");
    controllers.delete(key);
  };

  const abortAll = (): void => {
    controllers.forEach((ctrl) => ctrl.abort("Global abort"));
    controllers.clear();
  };

  return { authFetch, abortRequest, abortAll };
};
